import React, { FC } from "react";
import classNames from "classnames";
import { Button, ConfigProvider, Popover } from "antd";
// @ts-ignore
import { FormattedNumber } from "@umijs/max";

import styles from "./index.less";

import { ReactComponent as BasicIcon } from "../assets/icons/basic.svg";
import { ReactComponent as PlusIcon } from "../assets/icons/plus.svg";
import { ReactComponent as ProIcon } from "../assets/icons/pro.svg";
import { ReactComponent as UltraIcon } from "../assets/icons/ultra.svg";

import { ReactComponent as CheckIcon } from "../assets/svg/check.svg";

import { ReactComponent as BestSellerIcon } from "../assets/ribbons/best-seller.svg";
import { ReactComponent as MostPowerfulIcon } from "../assets/ribbons/most-powerful.svg";

import {
  PricingPeriodEnum,
  PricingPlanVersionEnum,
  PricingPlanVersionNumMap,
} from "../types";
import { useVersionWithPrice } from "../hooks";
import { getPriceByVersionAndPeriod } from "../utils";

import { BlackTheme } from "@packages/theme";

const PriceUnits: Record<PricingPeriodEnum, string> = {
  [PricingPeriodEnum.MONTHLY]: "/月",
  [PricingPeriodEnum.YEARLY]: "/年",
};

const PowerLabel = () => {
  return (
    <Popover
      content={
        <div className={styles["popover"]}>
          <span className={styles["popover-label"]}>动能值：</span>
          <span className={styles["popover-content"]}>
            1元 = 20点动能值，用于系统中所有 AI
            运作产生的消耗，可在平台充值账户中抵扣
          </span>
        </div>
      }
    >
      <div className={styles["highlight-gray"]}>动能值</div>
    </Popover>
  );
};

interface PlansProps {
  period: PricingPeriodEnum;
  paiedVersion?: PricingPlanVersionEnum;
  isExpired?: boolean;
  onSubscribe: (version: PricingPlanVersionEnum) => void;
  onRenew: (version: PricingPlanVersionEnum) => void;
  onUpgrade: (targetVersion: PricingPlanVersionEnum) => void;
}

const Plans: FC<PlansProps> = ({
  period,
  paiedVersion,
  isExpired,
  onSubscribe,
  onRenew,
  onUpgrade,
}) => {
  const paiedVersionNum = paiedVersion
    ? PricingPlanVersionNumMap[paiedVersion]
    : 0;

  const { map: VersionWithPriceMap } = useVersionWithPrice();

  return (
    <ConfigProvider theme={BlackTheme}>
      <div className={styles.plans}>
        <div
          className={classNames(styles.common, styles.basic, {
            [styles["common-month"]]: period === PricingPeriodEnum.MONTHLY,
          })}
        >
          <div className={styles.header}>
            <div className={styles.title}>
              <BasicIcon />
              <div className={styles.version}>Basic</div>
            </div>
            <div className={styles.description}>
              适用于博主、销售、专业服务人员等个人提高工作效率
            </div>
            <div className={styles.price}>
              <div className={styles.num}>
                ¥
                <FormattedNumber
                  value={getPriceByVersionAndPeriod(
                    VersionWithPriceMap,
                    PricingPlanVersionEnum.BASIC,
                    period
                  )}
                  style="decimal"
                />
              </div>
              <div className={styles.unit}>{PriceUnits[period]}</div>
            </div>

            <div
              className={classNames(styles.tip, {
                [styles["tip-empty"]]: period !== PricingPeriodEnum.YEARLY,
              })}
            >
              {period === PricingPeriodEnum.YEARLY
                ? "送 1 个提示词指点修改机器人"
                : ""}
            </div>

            {(paiedVersion === void 0 || isExpired) && (
              <Button
                type="primary"
                className={styles.button}
                onClick={() => {
                  onSubscribe(PricingPlanVersionEnum.BASIC);
                }}
              >
                立即订阅
              </Button>
            )}

            {!isExpired &&
              !!paiedVersion &&
              paiedVersion === PricingPlanVersionEnum.BASIC && (
                <Button
                  type="default"
                  className={styles.button}
                  onClick={() => {
                    onRenew(PricingPlanVersionEnum.BASIC);
                  }}
                >
                  续费当前订阅
                </Button>
              )}

            {!isExpired &&
              !!paiedVersion &&
              paiedVersionNum >
                PricingPlanVersionNumMap[PricingPlanVersionEnum.BASIC] && (
                <Popover content="若您需要订阅低版本服务，可前往资源中心的「自动续费」模块完成相关设置。">
                  <Button type="primary" className={styles.button} disabled>
                    更改订阅
                  </Button>
                </Popover>
              )}
          </div>
          <div className={styles.body}>
            <div className={styles.compare}>功能权益</div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>机器人 · 2个</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                问答机器人（预置）
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                接待机器人（预置）
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>通用权益额度</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                <PowerLabel />
                ：1千点/月
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                运营消息：1千条/次，1万条/月
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>客户管理</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                CRM
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                手动打标签
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>客户运营</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                单场景群发
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                客户全生命周期运营
              </div>
            </div>
          </div>
        </div>
        <div
          className={classNames(styles.common, styles.plus, {
            [styles["common-month"]]: period === PricingPeriodEnum.MONTHLY,
          })}
        >
          <BestSellerIcon className={styles.ribbon} />
          <div className={styles.header}>
            <div className={styles.title}>
              <PlusIcon />
              <div className={styles.version}>Plus</div>
            </div>
            <div className={styles.description}>
              适用于业务场景比较简单，重复手工操作较多，提升1-2个部门智能化的中小企业
            </div>
            <div className={styles.price}>
              <div className={styles.num}>
                ¥
                <FormattedNumber
                  value={getPriceByVersionAndPeriod(
                    VersionWithPriceMap,
                    PricingPlanVersionEnum.PLUS,
                    period
                  )}
                  style="decimal"
                />
              </div>
              <div className={styles.unit}>{PriceUnits[period]}</div>
            </div>

            <div
              className={classNames(styles.tip, {
                [styles["tip-empty"]]: period !== PricingPeriodEnum.YEARLY,
              })}
            >
              {period === PricingPeriodEnum.YEARLY
                ? "送 2 个小时编排在线指导"
                : ""}
            </div>

            {(paiedVersion === void 0 || isExpired) && (
              <Button
                type="primary"
                className={styles.button}
                onClick={() => {
                  onSubscribe(PricingPlanVersionEnum.PLUS);
                }}
              >
                立即订阅
              </Button>
            )}

            {!isExpired &&
              !!paiedVersion &&
              paiedVersion === PricingPlanVersionEnum.PLUS && (
                <Button
                  type="default"
                  className={styles.button}
                  onClick={() => {
                    onRenew(PricingPlanVersionEnum.PLUS);
                  }}
                >
                  续费当前订阅
                </Button>
              )}

            {!isExpired &&
              !!paiedVersion &&
              paiedVersionNum <
                PricingPlanVersionNumMap[PricingPlanVersionEnum.PLUS] && (
                <Button
                  type="primary"
                  className={styles.button}
                  onClick={() => {
                    onUpgrade(PricingPlanVersionEnum.PLUS);
                  }}
                >
                  立即升级
                </Button>
              )}

            {!isExpired &&
              !!paiedVersion &&
              paiedVersionNum >
                PricingPlanVersionNumMap[PricingPlanVersionEnum.PLUS] && (
                <Popover content="若您需要订阅低版本服务，可前往资源中心的「自动续费」模块完成相关设置。">
                  <Button type="primary" className={styles.button} disabled>
                    更改订阅
                  </Button>
                </Popover>
              )}
          </div>
          <div className={styles.body}>
            <div className={styles.compare}>包含Basic版全部权益，以及</div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>机器人 · 10个</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                促成跟进机器人（预置）
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                销售总结机器人（预置）
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                销售评价机器人（预置）
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                自定义机器人 · 5个
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                可编辑机器人流程账号 · 5个
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>通用权益额度</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                <PowerLabel />
                ：1万5千点/月
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                运营消息：3万条/次，50万条/月
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>客户管理</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                CRM支持<div className={styles.highlight}>手动API调用</div>
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                企微标签<div className={styles.highlight}>单向同步</div>
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>系统集成</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                支持&nbsp;
                <Popover
                  content={
                    <div className={styles["popover"]}>
                      <span className={styles["popover-label"]}>MCP：</span>
                      <span className={styles["popover-content"]}>
                        满足机器人自动调用各类线上工具完成任务
                      </span>
                      <br />
                      <span className={styles["popover-label"]}>API：</span>
                      <span className={styles["popover-content"]}>
                        满足机器人调取客户数据
                      </span>
                      <br />
                      <span className={styles["popover-label"]}>RPA：</span>
                      <span className={styles["popover-content"]}>
                        满足机器人操作电脑系统抓取数据或完成任务
                      </span>
                    </div>
                  }
                  placement="top"
                >
                  <div className={styles["highlight-dotted"]}>MCP/API/RPA</div>
                </Popover>
              </div>
            </div>
          </div>
        </div>
        <div
          className={classNames(styles.common, styles.pro, {
            [styles["common-month"]]: period === PricingPeriodEnum.MONTHLY,
          })}
        >
          <div className={styles.header}>
            <div className={styles.title}>
              <ProIcon />
              <div className={styles.version}>Pro</div>
            </div>
            <div className={styles.description}>
              适用于单一业务，场景复杂，想解决多个部门（前中后台）智能化的中小企业
            </div>
            <div className={styles.price}>
              <div className={styles.num}>
                ¥
                <FormattedNumber
                  value={getPriceByVersionAndPeriod(
                    VersionWithPriceMap,
                    PricingPlanVersionEnum.PRO,
                    period
                  )}
                  style="decimal"
                />
              </div>
              <div className={styles.unit}>{PriceUnits[period]}</div>
            </div>

            <div
              className={classNames(styles.tip, {
                [styles["tip-empty"]]: period !== PricingPeriodEnum.YEARLY,
              })}
            >
              {period === PricingPeriodEnum.YEARLY ? "送 1 个定制机器人" : ""}
            </div>

            {(paiedVersion === void 0 || isExpired) && (
              <Button
                type="primary"
                className={styles.button}
                onClick={() => {
                  onSubscribe(PricingPlanVersionEnum.PRO);
                }}
              >
                立即订阅
              </Button>
            )}

            {!isExpired &&
              !!paiedVersion &&
              paiedVersion === PricingPlanVersionEnum.PRO && (
                <Button
                  type="default"
                  className={styles.button}
                  onClick={() => {
                    onRenew(PricingPlanVersionEnum.PRO);
                  }}
                >
                  续费当前订阅
                </Button>
              )}

            {!isExpired &&
              !!paiedVersion &&
              paiedVersionNum <
                PricingPlanVersionNumMap[PricingPlanVersionEnum.PRO] && (
                <Button
                  type="primary"
                  className={styles.button}
                  onClick={() => {
                    onUpgrade(PricingPlanVersionEnum.PRO);
                  }}
                >
                  立即升级
                </Button>
              )}

            {!isExpired &&
              !!paiedVersion &&
              paiedVersionNum >
                PricingPlanVersionNumMap[PricingPlanVersionEnum.PRO] && (
                <Popover content="若您需要订阅低版本服务，可前往资源中心的「自动续费」模块完成相关设置。">
                  <Button type="primary" className={styles.button} disabled>
                    更改订阅
                  </Button>
                </Popover>
              )}
          </div>
          <div className={styles.body}>
            <div className={styles.compare}>包含Plus版全部权益，以及</div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>机器人 · 30个</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                智能标签机器人（预置）
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                自定义机器人 · 24个
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                可编辑机器人流程账号 · 15个
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>通用权益额度</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                <PowerLabel />
                ：5万点/月
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                运营消息：5万条/次，100万条/月
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>客户管理</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                企微标签<div className={styles.highlight}>双向同步</div>
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>客户运营</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                客户全生命周期运营：
                <div className={styles.highlight}>接入机器人</div>
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                非业务场景智能运营
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>系统集成</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                支持API调用：每分钟 ≤ 60次
              </div>
            </div>
          </div>
        </div>
        <div
          className={classNames(styles.common, styles.ultra, {
            [styles["common-month"]]: period === PricingPeriodEnum.MONTHLY,
          })}
        >
          <MostPowerfulIcon className={styles.ribbon} />
          <div className={styles.header}>
            <div className={styles.title}>
              <UltraIcon />
              <div className={styles.version}>Ultra</div>
            </div>
            <div className={styles.description}>
              适用于业务线较多，场景复杂，多业务多部门解决智能化的中小企业
            </div>
            <div className={styles.price}>
              <div className={styles.num}>
                ¥
                <FormattedNumber
                  value={getPriceByVersionAndPeriod(
                    VersionWithPriceMap,
                    PricingPlanVersionEnum.ULTRA,
                    period
                  )}
                  style="decimal"
                />
              </div>
              <div className={styles.unit}>{PriceUnits[period]}</div>
            </div>

            <div
              className={classNames(styles.tip, {
                [styles["tip-empty"]]: period !== PricingPeriodEnum.YEARLY,
              })}
            >
              {period === PricingPeriodEnum.YEARLY ? "送 3 个定制机器人" : ""}
            </div>

            {(paiedVersion === void 0 || isExpired) && (
              <Button
                type="primary"
                className={styles.button}
                onClick={() => {
                  onSubscribe(PricingPlanVersionEnum.ULTRA);
                }}
              >
                立即订阅
              </Button>
            )}

            {!isExpired &&
              !!paiedVersion &&
              paiedVersion === PricingPlanVersionEnum.ULTRA && (
                <Button
                  type="default"
                  className={styles.button}
                  onClick={() => {
                    onRenew(PricingPlanVersionEnum.ULTRA);
                  }}
                >
                  续费当前订阅
                </Button>
              )}

            {!isExpired &&
              !!paiedVersion &&
              paiedVersionNum <
                PricingPlanVersionNumMap[PricingPlanVersionEnum.ULTRA] && (
                <Button
                  type="primary"
                  className={styles.button}
                  onClick={() => {
                    onUpgrade(PricingPlanVersionEnum.ULTRA);
                  }}
                >
                  立即升级
                </Button>
              )}
          </div>
          <div className={styles.body}>
            <div className={styles.compare}>包含Pro版全部权益，以及</div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>机器人 · 不限量</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                销售机器人（预置）
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                客服机器人（预置）
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                自定义机器人&nbsp;·&nbsp;
                <div className={styles.highlight}>不限量</div>
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                可编辑机器人流程账号&nbsp;·&nbsp;
                <div className={styles.highlight}>不限量</div>
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>通用权益额度</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                <PowerLabel />
                ：12万点/月
              </div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                运营消息：10万条/次，250万条/月
              </div>
            </div>
            <div className={styles.hr}></div>
            <div className={styles.feature}>
              <div className={styles.title}>系统集成</div>
              <div className={styles.item}>
                <CheckIcon className={styles.icon} />
                支持API调用：每分钟 ≤ 100次
              </div>
            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Plans;
